import classNames from "classnames";
import * as React from "react";

import { Image } from "common/types/images";

import isValidFunction from "seneca-common/utils/functions/checks/isValidFunction";

import ImageComponent from "./ImageComponent";
import stylesCSS from "./ImageVideo.module.scss";
import VideoComponent from "./VideoComponent";

export const isHTMLVideo = (src: string | null | undefined): boolean => {
  return src ? src.endsWith(".mp4") : false;
};

export type { Image };

type CommonProps = {
  src?: string;
  image?: Image;
  alt?: string;
  errorComponent?: React.ComponentType<{}>;
  onError?: () => void;
  onLoad?: () => void;
  styles?: Record<string, any>;
  play?: boolean;
  elementClassName?: string;
  noWrapperDiv?: boolean;
  limitReplays: boolean;
};

type FillParentProps = CommonProps & {
  fillParent: true;
};

type DimensionsProps = CommonProps & {
  fillParent?: boolean;
  height?: string | number;
  width?: string | number;
  minHeight?: number;
  minWidth?: number;
  maxHeight?: number;
  maxWidth?: number;
  margin?: string | number;
};

export type ImageVideoProps = FillParentProps | DimensionsProps;

type State = {
  video: boolean;
  showErrorComponent: boolean;
};

class ImageVideo extends React.PureComponent<ImageVideoProps, State> {
  state = {
    video: isHTMLVideo(this.getImageSource()),
    showErrorComponent: false
  };

  componentDidUpdate(prevProps: ImageVideoProps) {
    if (
      prevProps.src !== this.props.src ||
      prevProps.image !== this.props.image
    ) {
      this.setState({
        video: isHTMLVideo(this.getImageSource())
      });
    }
  }

  getImageSource() {
    const { src, image } = this.props;

    if (src && typeof src === "string") {
      return src;
    } else if (image) {
      const { url } = image;
      return url;
    } else {
      return "";
    }
  }

  showErrorComponent = () =>
    this.setState({
      showErrorComponent: true
    });

  render() {
    if (this.state.showErrorComponent) {
      const { onError, errorComponent } = this.props;
      isValidFunction(onError) && onError();

      if (errorComponent) {
        const ErrorComponent = errorComponent;
        return <ErrorComponent />;
      }

      return null;
    }

    let styles = this.props.styles ? { ...this.props.styles } : {};

    if (!this.props.fillParent) {
      const {
        margin,
        height,
        width,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth
      } = this.props;
      if (height) {
        if (typeof height === "number") {
          styles.height = `${height}px`;
        } else {
          styles.height = height;
        }
      }
      if (width) {
        if (typeof width === "number") {
          styles.width = `${width}px`;
        } else {
          styles.width = width;
        }
      }
      if (maxHeight) styles.maxHeight = `${maxHeight}px`;
      if (maxWidth) styles.maxWidth = `${maxWidth}px`;
      if (minHeight) styles.minHeight = `${minHeight}px`;
      if (minWidth) styles.minWidth = `${minWidth}px`;
      if (margin) styles.margin = margin;
    }

    const {
      image,
      alt,
      fillParent,
      onLoad,
      play,
      elementClassName,
      noWrapperDiv,
      limitReplays
    } = this.props;

    if (this.state.video) {
      const Video = (
        <VideoComponent
          className={classNames(stylesCSS.video, elementClassName, {
            [stylesCSS.fillParent]: fillParent
          })}
          play={play}
          src={this.getImageSource()!}
          fallbackSrc={image && image.fallbackUrl}
          onLoad={onLoad}
          showErrorComponent={this.showErrorComponent}
          limitReplays={limitReplays}
        />
      );
      if (fillParent || noWrapperDiv) return Video;
      return (
        <div className={stylesCSS.videoWrapper} style={styles}>
          {Video}
        </div>
      );
    }

    return (
      <ImageComponent
        className={classNames(stylesCSS.image, elementClassName, {
          [stylesCSS.fillParent]: fillParent
        })}
        alt={alt ? alt : null}
        src={this.getImageSource()!}
        styles={styles}
        onLoad={onLoad}
        showErrorComponent={this.showErrorComponent}
      />
    );
  }
}

export default ImageVideo;
